import { supabase } from './supabaseClient';

/**
 * Получить текущего пользователя
 */
async function getCurrentUser() {
  const currentUserStr = localStorage.getItem('currentUser');
  if (currentUserStr) {
    try {
      const user = JSON.parse(currentUserStr);
      if (user && user.id) {
        // Пробуем teachers (для преподавателей)
        const { data: teacherData, error: teacherError } = await supabase
          .from('teachers')
          .select('*')
          .eq('auth_user_id', user.id)
          .maybeSingle();
        
        if (!teacherError && teacherData) {
          return {
            ...teacherData,
            id: teacherData.auth_user_id,
            role: 'teacher'
          };
        }
        
        // Если не преподаватель, возвращаем из localStorage
        return user;
      }
    } catch (e) {
      console.error('Error parsing currentUser from localStorage:', e);
    }
  }
  
  const { data: { user: authUser } } = await supabase.auth.getUser();
  if (authUser) {
    const { data: teacherData } = await supabase
      .from('teachers')
      .select('*')
      .eq('auth_user_id', authUser.id)
      .maybeSingle();
    
    if (teacherData) {
      return {
        ...teacherData,
        id: teacherData.auth_user_id,
        role: 'teacher'
      };
    }
  }
  
  return null;
}

/**
 * Конвертировать группу из localStorage формата в Supabase формат
 */
function convertGroupToSupabaseFormat(group) {
  return {
    id: group.id,
    teacher_id: null, // Будет установлено при сохранении
    name: group.name || '',
    email: group.email || null,
    phone: group.phone || null,
    proficiency: group.proficiency || null,
    price_per_student: group.pricePerStudent ? parseFloat(group.pricePerStudent.replace(/[^0-9.]/g, '')) : null,
    status: group.status || 'active',
    created_at: group.createdAt || new Date().toISOString(),
    updated_at: group.updatedAt || new Date().toISOString()
  };
}

/**
 * Конвертировать группу из Supabase формата в localStorage формат
 */
function convertGroupFromSupabaseFormat(group) {
  if (!group) return null;

  return {
    id: group.id,
    name: group.name || '',
    email: group.email || null,
    phone: group.phone || null,
    proficiency: group.proficiency || null,
    pricePerStudent: group.price_per_student ? `$${group.price_per_student}/h` : null,
    status: group.status || 'active',
    createdAt: group.created_at || new Date().toISOString(),
    updatedAt: group.updated_at || new Date().toISOString()
  };
}

/**
 * Fallback: загрузить группы из localStorage
 */
function getGroupsFromLocalStorage() {
  try {
    const stored = localStorage.getItem('novas_groups_data');
    return stored ? JSON.parse(stored) : {};
  } catch (error) {
    console.error('Error loading groups from localStorage:', error);
    return {};
  }
}

/**
 * Получить все группы текущего преподавателя
 */
export async function getGroups() {
  try {
    const user = await getCurrentUser();
    if (!user) {
      console.warn('User not authenticated, falling back to localStorage');
      return getGroupsFromLocalStorage();
    }

    // Получаем teacher.id по auth_user_id
    if (user.role !== 'teacher') {
      console.warn('User is not a teacher');
      return getGroupsFromLocalStorage();
    }
    
    const { data: teacher } = await supabase
      .from('teachers')
      .select('id')
      .eq('auth_user_id', user.id)
      .maybeSingle();
    
    if (!teacher) {
      console.warn('Teacher not found');
      return getGroupsFromLocalStorage();
    }
    
    const { data, error } = await supabase
      .from('groups')
      .select('*')
      .eq('teacher_id', teacher.id)
      .order('created_at', { ascending: false });

    if (error) {
      console.error('Error fetching groups from Supabase:', error);
      return getGroupsFromLocalStorage();
    }

    // Конвертируем в формат, совместимый с существующим кодом
    const groupsMap = {};
    data.forEach(group => {
      const converted = convertGroupFromSupabaseFormat(group);
      if (converted) {
        groupsMap[converted.id] = converted;
      }
    });

    return groupsMap;
  } catch (error) {
    console.error('Error in getGroups:', error);
    return getGroupsFromLocalStorage();
  }
}

/**
 * Получить группу по ID
 */
export async function getGroupById(groupId) {
  try {
    const user = await getCurrentUser();
    if (!user) {
      console.warn('User not authenticated, falling back to localStorage');
      const groups = getGroupsFromLocalStorage();
      return groups[groupId] || null;
    }

    // Получаем teacher.id по auth_user_id
    if (user.role !== 'teacher') {
      console.warn('User is not a teacher');
      const groups = getGroupsFromLocalStorage();
      return groups[groupId] || null;
    }
    
    const { data: teacher } = await supabase
      .from('teachers')
      .select('id')
      .eq('auth_user_id', user.id)
      .maybeSingle();
    
    if (!teacher) {
      console.warn('Teacher not found');
      const groups = getGroupsFromLocalStorage();
      return groups[groupId] || null;
    }
    
    const { data, error } = await supabase
      .from('groups')
      .select('*')
      .eq('id', groupId)
      .eq('teacher_id', teacher.id)
      .single();

    if (error) {
      console.error('Error fetching group from Supabase:', error);
      const groups = getGroupsFromLocalStorage();
      return groups[groupId] || null;
    }

    return convertGroupFromSupabaseFormat(data);
  } catch (error) {
    console.error('Error in getGroupById:', error);
    const groups = getGroupsFromLocalStorage();
    return groups[groupId] || null;
  }
}

/**
 * Создать группу
 */
export async function createGroup(groupData) {
  try {
    const user = await getCurrentUser();
    if (!user) {
      console.warn('User not authenticated, falling back to localStorage');
      const { saveGroupData } = await import('../assets/data');
      return saveGroupData(groupData);
    }

    // Получаем teacher.id по auth_user_id
    if (user.role !== 'teacher') {
      throw new Error('Only teachers can create groups');
    }
    
    const { data: teacher } = await supabase
      .from('teachers')
      .select('id')
      .eq('auth_user_id', user.id)
      .maybeSingle();
    
    if (!teacher) {
      throw new Error('Teacher not found');
    }
    
    const supabaseGroup = convertGroupToSupabaseFormat(groupData);
    supabaseGroup.teacher_id = teacher.id;
    supabaseGroup.updated_at = new Date().toISOString();

    const { data, error } = await supabase
      .from('groups')
      .insert(supabaseGroup)
      .select()
      .single();

    if (error) {
      console.error('Error creating group in Supabase:', error);
      // Fallback на localStorage
      const { saveGroupData } = await import('../assets/data');
      return saveGroupData(groupData);
    }

    // Также сохраняем в localStorage для обратной совместимости
    const savedGroup = convertGroupFromSupabaseFormat(data);
    const { saveGroupData } = await import('../assets/data');
    saveGroupData(savedGroup);

    return savedGroup;
  } catch (error) {
    console.error('Error in createGroup:', error);
    // Fallback на localStorage
    const { saveGroupData } = await import('../assets/data');
    return saveGroupData(groupData);
  }
}

/**
 * Обновить группу
 */
export async function updateGroup(groupId, updates) {
  try {
    const user = await getCurrentUser();
    if (!user) {
      console.warn('User not authenticated, falling back to localStorage');
      const { getGroupData, saveGroupData } = await import('../assets/data');
      const group = getGroupData(groupId);
      if (group) {
        const updated = { ...group, ...updates, updatedAt: new Date().toISOString() };
        saveGroupData(updated);
        return updated;
      }
      return null;
    }

    const supabaseUpdates = {
      name: updates.name,
      email: updates.email || null,
      phone: updates.phone || null,
      proficiency: updates.proficiency || null,
      price_per_student: updates.pricePerStudent ? parseFloat(updates.pricePerStudent.replace(/[^0-9.]/g, '')) : null,
      status: updates.status || 'active',
      updated_at: new Date().toISOString()
    };

    // Получаем teacher.id по auth_user_id
    if (user.role !== 'teacher') {
      throw new Error('Only teachers can update groups');
    }
    
    const { data: teacher } = await supabase
      .from('teachers')
      .select('id')
      .eq('auth_user_id', user.id)
      .maybeSingle();
    
    if (!teacher) {
      throw new Error('Teacher not found');
    }
    
    const { data, error } = await supabase
      .from('groups')
      .update(supabaseUpdates)
      .eq('id', groupId)
      .eq('teacher_id', teacher.id)
      .select()
      .single();

    if (error) {
      console.error('Error updating group in Supabase:', error);
      // Fallback на localStorage
      const { getGroupData, saveGroupData } = await import('../assets/data');
      const group = getGroupData(groupId);
      if (group) {
        const updated = { ...group, ...updates, updatedAt: new Date().toISOString() };
        saveGroupData(updated);
        return updated;
      }
      return null;
    }

    // Также обновляем в localStorage
    const savedGroup = convertGroupFromSupabaseFormat(data);
    const { saveGroupData } = await import('../assets/data');
    saveGroupData(savedGroup);

    return savedGroup;
  } catch (error) {
    console.error('Error in updateGroup:', error);
    // Fallback на localStorage
    const { getGroupData, saveGroupData } = await import('../assets/data');
    const group = getGroupData(groupId);
    if (group) {
      const updated = { ...group, ...updates, updatedAt: new Date().toISOString() };
      saveGroupData(updated);
      return updated;
    }
    return null;
  }
}

/**
 * Удалить группу
 */
export async function deleteGroup(groupId) {
  try {
    const user = await getCurrentUser();
    if (!user) {
      console.warn('User not authenticated, falling back to localStorage');
      const { deleteGroup: deleteGroupLocal } = await import('../assets/data');
      return deleteGroupLocal(groupId);
    }

    // Получаем teacher.id по auth_user_id
    if (user.role !== 'teacher') {
      throw new Error('Only teachers can delete groups');
    }
    
    const { data: teacher } = await supabase
      .from('teachers')
      .select('id')
      .eq('auth_user_id', user.id)
      .maybeSingle();
    
    if (!teacher) {
      throw new Error('Teacher not found');
    }
    
    const { error } = await supabase
      .from('groups')
      .delete()
      .eq('id', groupId)
      .eq('teacher_id', teacher.id);

    if (error) {
      console.error('Error deleting group from Supabase:', error);
      // Fallback на localStorage
      const { deleteGroup: deleteGroupLocal } = await import('../assets/data');
      return deleteGroupLocal(groupId);
    }

    // Также удаляем из localStorage
    const { deleteGroup: deleteGroupLocal } = await import('../assets/data');
    deleteGroupLocal(groupId);

    return true;
  } catch (error) {
    console.error('Error in deleteGroup:', error);
    // Fallback на localStorage
    const { deleteGroup: deleteGroupLocal } = await import('../assets/data');
    return deleteGroupLocal(groupId);
  }
}

